import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Shield,
  Users,
  BookOpen,
  Heart,
  Scale,
  Handshake,
  Mail,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage, translations } from "../context/LanguageContext";

const HomePage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;

  return (
    <div>
      {/* Hero: Direct, clear, no fluff */}
      <section className="bg-warm-800 text-white py-16 md:py-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl"
          >
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              data-testid="hero-title"
            >
              {t.hero.title}
            </h1>
            <p className="text-lg text-warm-200 leading-relaxed mb-4 max-w-xl">
              {t.hero.subtitle}
            </p>
            <p className="text-sm text-warm-300 leading-relaxed mb-8 max-w-xl">
              {t.hero.hubStatement}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="bg-white text-warm-800 hover:bg-warm-100 rounded-full px-8 py-6 text-base font-semibold" data-testid="hero-get-support">
                  {t.hero.getHelpButton} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/referrals">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-base" data-testid="hero-refer">
                  {t.hero.partnerButton}
                </Button>
              </Link>
              <Link to="/donate">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-base" data-testid="hero-donate">
                  {t.hero.donateButton}
                </Button>
              </Link>
              <Link to="/training">
                <Button variant="ghost" className="text-white hover:bg-white/10 rounded-full px-6 py-6 text-base underline underline-offset-4" data-testid="hero-training">
                  {t.hero.trainingButton}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-12 bg-warm-50 border-b border-warm-100" data-testid="service-categories">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: "Advocacy", desc: "Trauma-informed crisis support" },
              { label: "Referrals", desc: "Warm connections to trusted providers" },
              { label: "Workforce", desc: "Job readiness & professional pathways" },
              { label: "Training", desc: "Education for police, hospitals, and community" },
            ].map((cat) => (
              <motion.div
                key={cat.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <p className="text-2xl md:text-3xl font-bold text-warm-800" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                  {cat.label}
                </p>
                <p className="text-xs text-gray-600 mt-1 max-w-[180px] mx-auto">{cat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach: Listen -> Stabilize -> Connect -> Rebuild */}
      <section className="py-16 md:py-20" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10 max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">{t.pillars.overline}</span>
            <h2 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              {t.problem.title}
            </h2>
            <p className="text-gray-600">
              {t.problem.description}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, step: "01" },
              { icon: Shield, step: "02" },
              { icon: Handshake, step: "03" },
              { icon: BookOpen, step: "04" },
            ].map((svc, idx) => (
              <motion.div
                key={svc.step}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white border border-gray-100 rounded-xl p-6"
                data-testid={`approach-step-${idx}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <svc.icon className="w-8 h-8 text-warm-700" />
                  <span className="text-xs font-bold text-warm-400 tracking-wider">{svc.step}</span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                  {t.pillars.items[idx].title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{t.pillars.items[idx].description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8">
            <Link to="/services">
              <Button className="bg-warm-700 hover:bg-warm-800 text-white rounded-full px-8 py-5" data-testid="approach-learn-more">
                Explore Our Services <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              What We Provide Today
            </h2>
            <p className="text-gray-600 max-w-2xl">
              Direct advocacy, warm referrals, and workforce support — with follow-through survivors can rely on.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Direct Advocacy & Navigation",
                items: [
                  "Crisis intervention and safety planning",
                  "Trauma-informed advocacy and emotional support",
                  "Protective-order process guidance",
                  "Housing and shelter referrals",
                  "Mental-health resource navigation",
                  "Legal-resource navigation (referral only)",
                ],
              },
              {
                title: "Workforce & Stability",
                items: [
                  "Resume development and interview prep",
                  "Job-search and placement referrals",
                  "Professional-development coaching",
                  "Work-appropriate clothing coordination",
                  "Hospitality pathways with ServSafe (when available)",
                  "Ongoing follow-up to sustain employment",
                ],
              },
              {
                title: "Professional Training",
                items: [
                  "Human trafficking indicators on DV calls",
                  "Trauma-informed communication for first responders",
                  "Survivor-centered intervention practices",
                  "Referral and resource-navigation training",
                  "Delivered to police, hospitals, and community groups",
                  "Includes co-presentation with survivor leaders",
                ],
              },
            ].map((col, idx) => (
              <motion.div
                key={col.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white border border-gray-100 rounded-xl p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                  {col.title}
                </h3>
                <ul className="space-y-2">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/services">
              <Button className="bg-warm-700 hover:bg-warm-800 text-white rounded-full px-8 py-5" data-testid="services-view-all">
                See All Services <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/vision">
              <Button variant="outline" className="border-warm-700 text-warm-800 hover:bg-warm-50 rounded-full px-8 py-5" data-testid="services-vision">
                Where We're Going
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Community Resource Network */}
      <section className="py-16 md:py-20" data-testid="community-network">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">Community Resource Network</span>
              <h2 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                We Work Inside a Broader Ecosystem
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Pillar Project maintains professional relationships with organizations and service providers throughout Maryland.
                Depending on a survivor's circumstances, eligibility, location, and provider availability, we may help connect individuals
                with organizations offering shelter, legal support, counseling, family services, rapid rehousing, or other specialized assistance.
              </p>
              <p className="text-xs text-gray-500 italic">
                Referral connections are made based on survivor consent and provider capacity. The Pillar Project does not guarantee placement, funding, or acceptance into any external program.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-warm-50 border border-warm-100 rounded-xl p-6"
            >
              <p className="text-sm font-semibold text-warm-800 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                Types of Providers in Our Referral Network
              </p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" /> Emergency shelter</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" /> Legal-services orgs</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" /> Trauma-informed counseling</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" /> Rapid-rehousing programs</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" /> Family Justice Centers</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" /> Human-trafficking navigators</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" /> Sexual-assault advocacy</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" /> Baltimore City DSS</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resources & Crisis Links */}
      <section className="py-16 md:py-20" data-testid="resources-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              Resources
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-red-50 border border-red-100 rounded-xl p-6">
              <h3 className="text-base font-semibold text-red-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                Crisis Hotlines
              </h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="tel:1-800-799-7233" className="font-semibold text-red-800 hover:underline">National DV Hotline: 1-800-799-7233</a>
                  <p className="text-red-700 text-xs mt-0.5">Available 24/7. TTY: 1-800-787-3224</p>
                </li>
                <li>
                  <a href="tel:1-888-373-7888" className="font-semibold text-red-800 hover:underline">Human Trafficking Hotline: 1-888-373-7888</a>
                </li>
                <li>
                  <a href="tel:988" className="font-semibold text-red-800 hover:underline">Suicide & Crisis Lifeline: 988</a>
                </li>
                <li>
                  <a href="tel:211" className="font-semibold text-red-800 hover:underline">Maryland Crisis Line: 211</a>
                </li>
              </ul>
            </div>

            <div className="bg-warm-50 border border-warm-100 rounded-xl p-6">
              <h3 className="text-base font-semibold text-warm-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                Local Support
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex justify-between"><span>House of Ruth Maryland</span><a href="tel:410-889-0840" className="text-warm-700 font-medium">410-889-0840</a></li>
                <li className="flex justify-between"><span>TurnAround Inc</span><a href="tel:443-279-0379" className="text-warm-700 font-medium">443-279-0379</a></li>
                <li className="flex justify-between"><span>Legal Aid Bureau</span><a href="tel:410-539-5340" className="text-warm-700 font-medium">410-539-5340</a></li>
                <li className="flex justify-between"><span>Baltimore City DSS</span><a href="tel:443-423-6300" className="text-warm-700 font-medium">443-423-6300</a></li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-xl p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                Get Involved
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Volunteer, donate, or partner with us to support survivors in Baltimore.
              </p>
              <div className="space-y-2">
                <Link to="/donate" className="flex items-center gap-2 text-sm text-warm-700 font-medium hover:underline">
                  <Heart className="w-4 h-4" /> Make a Donation
                </Link>
                <Link to="/services" className="flex items-center gap-2 text-sm text-warm-700 font-medium hover:underline">
                  <BookOpen className="w-4 h-4" /> Education Programs
                </Link>
                <Link to="/careers" className="flex items-center gap-2 text-sm text-warm-700 font-medium hover:underline">
                  <Users className="w-4 h-4" /> Volunteer / Careers
                </Link>
                <a href="mailto:director@thepillarprojectbaltimore.org" className="flex items-center gap-2 text-sm text-warm-700 font-medium hover:underline">
                  <Mail className="w-4 h-4" /> Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-warm-800 text-white text-center" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-medium mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            You are not alone. Help is available now.
          </h2>
          <p className="text-warm-200 mb-6">
            Reach out to us or call the National Domestic Violence Hotline at any time.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-warm-800 hover:bg-warm-100 rounded-full px-8 py-5 font-semibold">
                Get Support <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:1-800-799-7233">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-5">
                1-800-799-7233
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
